import { useState } from "react";
// import VulnerabilityModal from "../../modals/VulnerabilityModal";
import { FiInfo } from "react-icons/fi";
import CircularScore from "../../../../../components/CircularScore";
import { Button } from "../../../../../components/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  acceptVulnerability,
  resolveVulnerability,
} from "../../../../../helpers/api";
import { toast } from "react-toastify";
import VulnerabilityDetails from "../../modals/VulnerabilityDetails";
import ThreatBadge from "../../../../../components/ThreatBadge";
import DataTable from "../../../../../components/DataTable";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../components/ui/select";
import { severities } from "../../../../../constants";
import SelectionActionBar from "../../../../../components/SelectionActionBar";

const Vulnerabilities = ({ results }) => {
  const [isOpen, setIsOpen] = useState(false);

  const [details, setDetails] = useState(null);
  const [threatFilter, setThreatFilter] = useState("");

  const [selectedIds, setSelectedIds] = useState([]);

  // allIds always reflects current results
  // const allIds = useMemo(() => results?.map((r) => r.id) || [], [results]);

  // const isIndeterminate =
  //   selectedIds.length > 0 && selectedIds.length < allIds.length;
  // const isAllSelected = selectedIds.length === allIds.length;

  // const toggleSelectAll = () => {
  //   if (isIndeterminate || isAllSelected) {
  //     setSelectedIds([]); // clear all
  //   } else {
  //     setSelectedIds(allIds); // select all
  //   }
  // };

  const queryClient = useQueryClient();
  const { mutate: accept, isPending: acceptPending } = useMutation({
    mutationFn: (id) => acceptVulnerability(id),
    onSuccess: (data) => {
      toast(data.message);
      setSelectedIds([])
      queryClient.invalidateQueries({ queryKey: ["scan-report"] });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to accept vulnerability");
    },
  });

  const { mutate: resolve, isPending: resolvePending } = useMutation({
    mutationFn: (id) => resolveVulnerability(id),
    onSuccess: (data) => {
      toast(data.message);
      setSelectedIds([])
      queryClient.invalidateQueries({ queryKey: ["scan-report"] });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to resolve vulnerability");
    },
  });

  const columns = [
    {
      accessorKey: "vulnerability_name",
      header: () => <span>Vulnerability</span>,
      width: "50%",
      cell: ({ row }) => <span>{row.original.vulnerability_name}</span>,
    },
    {
      accessorKey: "threat_level",
      header: () => <span>Severity</span>,
      width: "15%",
      cell: ({ row }) => (
        <span>
          {" "}
          <ThreatBadge threat={row.original.threat_level} />
        </span>
      ),
    },
    {
      accessorKey: "severity",
      header: () => <span>CVSS</span>,
      width: "18%",
      cell: ({ row }) => (
        <span>
          {" "}
          <CircularScore score={row.original?.severity} />
        </span>
      ),
    },
    {
      accessorKey: "qod",
      header: () => <span>QOD</span>,
      width: "12%",
      cell: ({ row }) => <span>{row.original.qod}</span>,
    },
    {
      id: "status",
      accessorFn: (row) => {
        // Return a clean, searchable string value
        if (row.status === "pending") return "Unresolved";
        if (row.status === "resolved") return "Resolved";
        return row.status || "Unknown";
      },
      header: () => <span>Status</span>,
      width: "20%",
      cell: ({ row }) => {
        const item = row.original;
        return item?.status === "pending" ? (
          <p className="border-cream100 text-brown100 border-[1.5px] rounded-full bg-[#FFBB331A] w-fit px-2 py-1 mx-auto ">
            Unresolved
          </p>
        ) : item?.status === "resolved" ? (
          <p className="border-[#d3e8f1] text-[#0BA5EC] border-[1.5px] rounded-full bg-[#F1F8FF] w-fit px-2 py-1 mx-auto ">
            Resolved
          </p>
        ) : (
          <p className="bg-[#FF44441A] text-[#E12A5B] border-[1.5px] rounded-full border-[#FFC1C1] w-fit px-2 py-1 mx-auto capitalize">
            {item?.status}
          </p>
        );
      },
    },
    {
      accessorKey: "hosts",
      header: () => <span>Host</span>,
      width: "20%",
      cell: ({ row }) => <span>{row.original.hosts}</span>,
    },
    {
      accessorKey: "port",
      header: () => <span>Port</span>,
      width: "20%",
      cell: ({ row }) => <span>{row.original.port ?? "--"}</span>,
    },
    {
      id: "action",
      header: () => <span className="block text-center"></span>,
      width: "8%",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <span className="block text-center">
            <FiInfo
              className="text-blue-600 cursor-pointer"
              onClick={() => {
                setDetails(item);
                setIsOpen(true);
              }}
            />
          </span>
        );
      },
    },
  ];

  return (
    <div>
      {results && results.length > 0 && (
 
        <>
          <SelectionActionBar
            count={selectedIds.length}
            label="assets"
            actions={
              <div className="flex items-center gap-4">
                <Button
                  onClick={() => accept(selectedIds)}
                  className="text-sm cursor-pointer text-red-500 hover:text-red-700 font-medium"
                  variant={"ghost"}
                  size={"ghost"}
                  loading={acceptPending}
                >
                  Accept risk
                </Button>
                <Button
                  onClick={() => resolve(selectedIds)}
                  className="text-sm cursor-pointer text-blue-500 hover:text-blue-700 font-medium"
                  variant={"ghost"}
                  size={"ghost"}
                  loading={resolvePending}
                >
                  Resolve risk
                </Button>
              </div>
            }
          />
          <DataTable
            columns={columns}
            data={results}
            externalFilters={{ threat_level: threatFilter || "" }}
            filtersSlot={
              <>
                <Select
                  value={threatFilter}
                  onValueChange={(value) =>
                    value === "all"
                      ? setThreatFilter("")
                      : setThreatFilter(value)
                  }
                >
                  <SelectTrigger
                    className="w-[120px] rounded-lg h-9"
                    aria-label="Severity"
                  >
                    <SelectValue placeholder="Severity" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {severities.map((item, index) => (
                      <SelectItem
                        value={item.value}
                        key={index}
                        className="rounded-lg"
                      >
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            }
            enableRowSelection
            onSelectionChange={setSelectedIds}
          />
        </>
      )}
      {results && results.length === 0 && (
        <div className=" h-[45vh] w-full flex justify-center flex-col items-center text-center">
          <p className=" font-semibold text-xl">There are no results</p>
        </div>
      )}
      <VulnerabilityDetails
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        details={details}
      />
    </div>
  );
};

export default Vulnerabilities;
